/**
 * Application Tracker Page - Combined Applications, Drafts & Documents Hub
 * Unified view showing Active Applications, Drafts to Apply, and Bookmarked courses
 */
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  FileText,
  FileSearch,
  CheckCircle,
  ClipboardList,
  ClipboardCheck,
  Send,
  Clock,
  Award,
  ThumbsUp,
  CreditCard,
  FileCheck,
  Plane,
  PartyPopper,
  XCircle,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  MessageCircle,
  Calendar,
  GraduationCap,
  Building,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Upload,
  Eye,
  Shield,
  Sparkles,
  TrendingUp,
  AlertCircle,
  Bookmark,
  BookmarkCheck,
  ArrowRight,
  Trash2,
  DollarSign,
  Search,
  Info,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

// Stages where student can cancel their application
const PRE_REVIEW_STAGES = ['applied', 'docs_review'];

// Stage icons mapping
const STAGE_ICONS = {
  applied: FileText,
  docs_review: FileSearch,
  docs_complete: CheckCircle,
  uaf_pending: ClipboardList,
  uaf_complete: ClipboardCheck,
  submitted_to_uni: Send,
  under_review: Clock,
  offer_received: Award,
  offer_accepted: ThumbsUp,
  fee_payment: CreditCard,
  coe_issued: FileCheck,
  visa_applied: Plane,
  visa_granted: PartyPopper,
  visa_rejected: XCircle,
  offer_rejected: XCircle
};

// Stage colors
const STAGE_COLORS = {
  blue: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-500/20 dark:text-blue-400 dark:border-blue-500/30',
  orange: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/20 dark:text-amber-400 dark:border-amber-500/30',
  green: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-400 dark:border-emerald-500/30',
  purple: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-500/20 dark:text-purple-400 dark:border-purple-500/30',
  red: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-500/20 dark:text-red-400 dark:border-red-500/30'
};

// Document types with metadata
const DOCUMENT_TYPES = {
  aadhaar: { name: 'Aadhaar Card', description: 'Identity verification', category: 'identity', icon: Shield },
  passport: { name: 'Passport', description: 'Travel document', category: 'identity', icon: Plane },
  '10th_marksheet': { name: '10th Marksheet', description: 'Secondary education', category: 'academic_ug', icon: GraduationCap },
  '12th_marksheet': { name: '12th Marksheet', description: 'Higher secondary', category: 'academic_ug', icon: GraduationCap },
  degree_certificate: { name: 'Degree Certificate', description: 'Graduation', category: 'academic_pg', icon: Award },
  transcripts: { name: 'Academic Transcripts', description: 'Semester-wise marks', category: 'academic_pg', icon: FileText },
  english_scorecard: { name: 'English Scorecard', description: 'IELTS/PTE/TOEFL', category: 'english', icon: FileCheck }
};

// Intake options
const INTAKE_TERMS = ['January', 'February', 'May', 'July', 'September', 'October'];
const INTAKE_YEARS = ['2025', '2026', '2027'];

const ApplicationTrackerPage = () => {
  const { isDark } = useTheme();
  const { user, token, loading: authLoading, isAuthenticated } = useStudentAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [bookmarked, setBookmarked] = useState([]);
  const [stats, setStats] = useState(null);
  const [stages, setStages] = useState([]);
  const [expandedApp, setExpandedApp] = useState(null);
  const [activeTab, setActiveTab] = useState('timeline');
  const [mainTab, setMainTab] = useState('active');
  
  // Documents state
  const [documents, setDocuments] = useState({});
  const [uploading, setUploading] = useState(null);
  
  // Modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [appToCancel, setAppToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  // Manual-add application dialog
  const [showAddManualModal, setShowAddManualModal] = useState(false);
  const [creatingManual, setCreatingManual] = useState(false);
  const [manualForm, setManualForm] = useState({
    university_name: '',
    program: '',
    country: '',
    intake_term: '',
    intake_year: '',
    tuition_fee: '',
    notes: '',
    status: 'draft', // draft | bookmarked
  });

  // DigiLocker verification status (profile-level, used by manual add prompts)
  const [digiLockerStatus, setDigiLockerStatus] = useState({ verified: false, count: 0 });
  const [initiatingDigiLocker, setInitiatingDigiLocker] = useState(false);
  const [draftToSubmit, setDraftToSubmit] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Helper to open Zoho SalesIQ chat
  const openCounselorChat = () => {
    // Try to open Zoho SalesIQ chat widget
    if (window.$zoho && window.$zoho.salesiq && window.$zoho.salesiq.chat) {
      window.$zoho.salesiq.chat.open();
      toast.success('Opening chat with your counselor...');
    } else {
      // Fallback if SalesIQ is not loaded
      toast.info('Chat is loading, please try again in a moment.');
    }
  };
  
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/uac-desk/login');
    }
  }, [authLoading, isAuthenticated, navigate]);
  
  const loadData = useCallback(async () => {
    if (!user?.user_id) return;
    setLoading(true);
    try {
      const [dashboardRes, stagesRes, profileRes, digiRes] = await Promise.all([
        fetch(`${API_URL}/api/applications/dashboard/${user.user_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/applications/stages`),
        fetch(`${API_URL}/api/counselor/profile/${user.user_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/digilocker/student/${user.user_id}/verified-documents`),
      ]);
      
      if (dashboardRes.ok) {
        const data = await dashboardRes.json();
        setApplications(data.applications || []);
        setDrafts(data.drafts || []);
        setBookmarked(data.bookmarked || []);
        setStats(data.stats);
      }
      
      if (stagesRes.ok) {
        const data = await stagesRes.json();
        setStages(data.stages || []);
      }
      
      if (profileRes.ok) {
        const profile = await profileRes.json();
        setDocuments(profile.documents || {});
      }

      if (digiRes.ok) {
        const dl = await digiRes.json();
        setDigiLockerStatus({
          verified: (dl.total || 0) > 0,
          count: dl.total || 0,
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [user, token]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const handleFileUpload = async (docType, file) => {
    if (!file) return;
    
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF, JPG, and PNG files are allowed');
      return;
    }
    
    setUploading(docType);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docType);
      
      const res = await fetch(`${API_URL}/api/counselor/upload-document/${user.user_id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast.success(`${DOCUMENT_TYPES[docType]?.name || docType} uploaded!`);
        setDocuments(prev => ({
          ...prev,
          [docType]: {
            uploaded: true,
            filename: file.name,
            uploaded_at: new Date().toISOString(),
            file_size: file.size
          }
        }));
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to upload document');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
    }
    
    setUploading(null);
  };
  
  // Check if required documents are uploaded.
  // Categories use OR-logic — any ONE doc in the group satisfies the category.
  const checkRequiredDocs = (programLevel = 'ug') => {
    const hasIdentity = documents.aadhaar?.uploaded || documents.passport?.uploaded;

    let hasAcademic = false;
    if (programLevel === 'ug') {
      hasAcademic = documents['10th_marksheet']?.uploaded || documents['12th_marksheet']?.uploaded;
    } else {
      hasAcademic = documents['12th_marksheet']?.uploaded ||
                   documents.degree_certificate?.uploaded ||
                   documents.transcripts?.uploaded;
    }
    const hasEnglish = documents.english_score?.uploaded || documents.english_scorecard?.uploaded;

    // Category-level breakdown for the readiness counter
    const categories = [
      { key: 'identity', satisfied: !!hasIdentity, required: true },
      { key: 'academic', satisfied: !!hasAcademic, required: true },
      { key: 'english', satisfied: !!hasEnglish, required: false },
    ];
    const satisfiedRequired = categories.filter(c => c.required && c.satisfied).length;
    const totalRequired = categories.filter(c => c.required).length;
    // For display, also count the optional English category as a bonus slot
    const satisfiedAll = categories.filter(c => c.satisfied).length;
    const totalAll = categories.length;

    return {
      hasIdentity,
      hasAcademic,
      hasEnglish,
      canSubmit: hasIdentity && hasAcademic,
      // Counters for the readiness UI
      readyCount: satisfiedAll,
      readyTotal: totalAll,
      requiredCount: satisfiedRequired,
      requiredTotal: totalRequired,
    };
  };
  
  const updateDraftIntake = async (appId, field, value) => {
    try {
      const res = await fetch(`${API_URL}/api/applications/draft/${appId}/update`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ [field]: value })
      });
      
      if (res.ok) {
        // Update local state
        setDrafts(prev => prev.map(d => 
          d.application_id === appId ? { ...d, [field]: value } : d
        ));
      }
    } catch (error) {
      console.error('Error updating draft:', error);
    }
  };
  
  const handleSubmitDraft = async () => {
    if (!draftToSubmit) return;
    
    // Check required documents
    const { canSubmit, hasIdentity, hasAcademic } = checkRequiredDocs();
    if (!canSubmit) {
      let missing = [];
      if (!hasIdentity) missing.push('Identity document (Aadhaar or Passport)');
      if (!hasAcademic) missing.push('Academic document (Marksheet/Transcript)');
      toast.error(`Please upload: ${missing.join(', ')}`);
      return;
    }
    
    // Check intake selection
    if (!draftToSubmit.intake_term || !draftToSubmit.intake_year) {
      toast.error('Please select intake term and year');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/applications/draft/${draftToSubmit.application_id}/submit?user_id=${user.user_id}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        toast.success(`Application to ${draftToSubmit.university_name} submitted!`);
        setShowSubmitModal(false);
        setDraftToSubmit(null);
        loadData(); // Refresh all data
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleMoveToDrafts = async (appId) => {
    try {
      const res = await fetch(`${API_URL}/api/applications/draft/${appId}/move-to-drafts`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Moved to drafts');
        loadData();
      }
    } catch (error) {
      console.error('Error moving to drafts:', error);
      toast.error('Failed to move to drafts');
    }
  };
  
  const handleMoveToBookmarks = async (appId) => {
    try {
      const res = await fetch(`${API_URL}/api/applications/draft/${appId}/move-to-bookmarks`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Moved to bookmarks');
        loadData();
      }
    } catch (error) {
      console.error('Error moving to bookmarks:', error);
      toast.error('Failed to move to bookmarks');
    }
  };

  const handleStartDigiLocker = async () => {
    if (!user?.user_id) {
      toast.error('Please login to connect DigiLocker');
      return;
    }
    setInitiatingDigiLocker(true);
    try {
      const res = await fetch(`${API_URL}/api/digilocker/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: user.user_id, signup_flow: false }),
      });
      const data = await res.json();
      if (data.success && data.authorization_url) {
        sessionStorage.setItem('digilocker_client_id', data.client_id);
        // After Surepass callback redirects to DigiLockerCallback.jsx, return user here
        sessionStorage.setItem('digilocker_return_to', '/uac-desk/applications');
        toast.info('Redirecting to DigiLocker…');
        window.location.href = data.authorization_url;
      } else {
        toast.error(data.message || data.detail || 'Failed to initiate DigiLocker');
      }
    } catch (e) {
      console.error('DigiLocker initiate error:', e);
      toast.error('Failed to connect to DigiLocker');
    } finally {
      setInitiatingDigiLocker(false);
    }
  };

  const handleCreateManualApplication = async () => {
    if (!manualForm.university_name.trim() || !manualForm.program.trim() || !manualForm.country.trim()) {
      toast.error('Please fill university, program, and country');
      return;
    }
    setCreatingManual(true);
    try {
      const res = await fetch(`${API_URL}/api/applications/draft/${user.user_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          university_name: manualForm.university_name.trim(),
          program: manualForm.program.trim(),
          country: manualForm.country.trim(),
          intake_term: manualForm.intake_term || null,
          intake_year: manualForm.intake_year || null,
          tuition_fee: manualForm.tuition_fee.trim() || null,
          notes: manualForm.notes.trim() || null,
          status: manualForm.status,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to create application');
        return;
      }
      const data = await res.json();
      const autoCount = data?.auto_attached_documents?.count || 0;
      const digiCount = data?.auto_attached_documents?.digilocker_verified_count || 0;
      let successMsg = manualForm.status === 'draft'
        ? `Draft created for ${manualForm.university_name}`
        : `Bookmarked ${manualForm.university_name}`;
      if (autoCount > 0) {
        successMsg += digiCount > 0
          ? ` • ${autoCount} doc${autoCount === 1 ? '' : 's'} auto-attached (${digiCount} DigiLocker-verified)`
          : ` • ${autoCount} doc${autoCount === 1 ? '' : 's'} auto-attached`;
      }
      toast.success(successMsg);
      // Reset and close
      setManualForm({
        university_name: '',
        program: '',
        country: '',
        intake_term: '',
        intake_year: '',
        tuition_fee: '',
        notes: '',
        status: 'draft',
      });
      setShowAddManualModal(false);
      setMainTab(data.status === 'bookmarked' ? 'bookmarked' : 'drafts');
      loadData();
    } catch (e) {
      console.error('Error creating manual application:', e);
      toast.error('Failed to create application');
    } finally {
      setCreatingManual(false);
    }
  };
  
  const handleDeleteDraft = async () => {
    if (!itemToDelete) return;
    
    setDeleting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/applications/draft/${itemToDelete.application_id}?user_id=${user.user_id}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        toast.success('Removed successfully');
        setShowDeleteModal(false);
        setItemToDelete(null);
        loadData();
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };
  
  const handleCancelApplication = async () => {
    if (!appToCancel) return;
    
    setCancelling(true);
    try {
      const res = await fetch(
        `${API_URL}/api/applications/${appToCancel.application_id}/cancel?user_id=${user.user_id}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        toast.success(`Application cancelled`);
        setShowCancelModal(false);
        setAppToCancel(null);
        loadData();
      }
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error('Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };
  
  const getStageIcon = (stageId) => STAGE_ICONS[stageId] || FileText;
  const getStageColor = (stageInfo) => STAGE_COLORS[stageInfo?.color] || STAGE_COLORS.blue;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  
  // Calculate document stats — category-based (Identity / Academic / English),
  // any ONE doc in a category satisfies it.
  const docsCheck = checkRequiredDocs();
  const uploadedCount = docsCheck.readyCount;
  const totalDocs = docsCheck.readyTotal;
  const docsReady = docsCheck.canSubmit;
  
  if (authLoading || loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f1a1f]' : 'bg-slate-50'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
      </div>
    );
  }
  
  return (
    <div className={`min-h-screen w-full ${isDark ? 'bg-[#0f1a1f]' : 'bg-slate-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        
        {/* Header - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-3">
            <Link to="/uac-desk/dashboard" data-testid="back-to-dashboard">
              <Button variant="ghost" size="icon" className="rounded-full h-9 w-9">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                My Applications
              </h1>
              <p className={`text-xs sm:text-sm ${isDark ? 'text-white/60' : 'text-slate-500'} hidden sm:block`}>
                Manage your applications, drafts & documents
              </p>
            </div>
          </div>
          <div className="flex gap-2 ml-12 sm:ml-0">
            <Button variant="outline" onClick={loadData} className="rounded-full h-9 w-9 p-0" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowAddManualModal(true)}
              className="rounded-full h-9 text-sm px-4"
              data-testid="add-manual-application-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> <span className="hidden xs:inline">Add</span> Manually
            </Button>
            <Link to="/uac-desk/ai-finder" data-testid="ai-finder-btn">
              <Button className="bg-ux-cyan hover:bg-ux-cyan/90 text-white rounded-full h-9 text-sm px-4">
                <Search className="w-4 h-4 mr-1.5" /> <span className="hidden xs:inline">Find</span> Courses
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Stats Overview - Mobile Optimized Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3 mb-4 sm:mb-6">
          <Card 
            className={`cursor-pointer transition-all hover:scale-[1.02] ${mainTab === 'active' ? 'ring-2 ring-ux-cyan' : ''} ${isDark ? 'bg-white/5 border-white/10' : 'bg-white'}`}
            onClick={() => setMainTab('active')}
            data-testid="stat-active"
          >
            <CardContent className="p-3 sm:p-4 text-center">
              <div className="text-xl sm:text-2xl font-bold text-ux-cyan">{stats?.active || 0}</div>
              <div className={`text-[10px] sm:text-xs ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Active</div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:scale-[1.02] ${mainTab === 'drafts' ? 'ring-2 ring-amber-500' : ''} ${isDark ? 'bg-white/5 border-white/10' : 'bg-white'}`}
            onClick={() => setMainTab('drafts')}
            data-testid="stat-drafts"
          >
            <CardContent className="p-3 sm:p-4 text-center">
              <div className="text-xl sm:text-2xl font-bold text-amber-500">{stats?.drafts || 0}</div>
              <div className={`text-[10px] sm:text-xs ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Drafts</div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:scale-[1.02] ${mainTab === 'bookmarked' ? 'ring-2 ring-purple-500' : ''} ${isDark ? 'bg-white/5 border-white/10' : 'bg-white'}`}
            onClick={() => setMainTab('bookmarked')}
            data-testid="stat-bookmarked"
          >
            <CardContent className="p-3 sm:p-4 text-center">
              <div className="text-xl sm:text-2xl font-bold text-purple-500">{stats?.bookmarked || 0}</div>
              <div className={`text-[10px] sm:text-xs ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Saved</div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:scale-[1.02] ${mainTab === 'offers' ? 'ring-2 ring-emerald-500' : ''} ${isDark ? 'bg-white/5 border-white/10' : 'bg-white'}`}
            onClick={() => setMainTab('offers')}
            data-testid="stat-offers"
          >
            <CardContent className="p-3 sm:p-4 text-center">
              <div className="text-xl sm:text-2xl font-bold text-emerald-500">{stats?.offers_received || 0}</div>
              <div className={`text-[10px] sm:text-xs ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Offers</div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:scale-[1.02] ${mainTab === 'documents' ? 'ring-2 ring-blue-500' : ''} ${isDark ? 'bg-white/5 border-white/10' : 'bg-white'}`}
            onClick={() => setMainTab('documents')}
            data-testid="stat-documents"
          >
            <CardContent className="p-3 sm:p-4 text-center">
              <div className="text-xl sm:text-2xl font-bold text-blue-500" data-testid="docs-readiness-count">{uploadedCount}/{totalDocs}</div>
              <div className={`text-[10px] sm:text-xs ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Doc categories</div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:scale-[1.02] ${isDark ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-emerald-50 border-emerald-100'}`}
            onClick={() => setMainTab('documents')}
            data-testid="stat-status"
          >
            <CardContent className="p-3 sm:p-4 text-center">
              <div className="flex items-center justify-center gap-1">
                {docsReady ? <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" /> : <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-500" />}
                <span className={`text-base sm:text-lg font-bold ${docsReady ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {docsReady ? 'Ready' : 'Pending'}
                </span>
              </div>
              <div className={`text-[10px] sm:text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Status</div>
            </CardContent>
          </Card>
        </div>
        
        {/* Main Content Tabs - Mobile Scrollable */}
        <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className={`inline-flex w-auto sm:w-full sm:grid sm:grid-cols-5 mb-4 ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
              <TabsTrigger value="active" className="whitespace-nowrap text-xs sm:text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-white/10 px-3 sm:px-4" data-testid="tab-active">
                <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" /> Active ({stats?.active || 0})
              </TabsTrigger>
              <TabsTrigger value="drafts" className="whitespace-nowrap text-xs sm:text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-white/10 px-3 sm:px-4" data-testid="tab-drafts">
                <ClipboardList className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" /> Drafts ({stats?.drafts || 0})
              </TabsTrigger>
              <TabsTrigger value="bookmarked" className="whitespace-nowrap text-xs sm:text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-white/10 px-3 sm:px-4" data-testid="tab-bookmarked">
                <Bookmark className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" /> Saved ({stats?.bookmarked || 0})
              </TabsTrigger>
              <TabsTrigger value="offers" className="whitespace-nowrap text-xs sm:text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-white/10 px-3 sm:px-4" data-testid="tab-offers">
                <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" /> Offers ({stats?.offers_received || 0})
              </TabsTrigger>
              <TabsTrigger value="documents" className="whitespace-nowrap text-xs sm:text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-white/10 px-3 sm:px-4" data-testid="tab-documents">
                <FileCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" /> Docs
              </TabsTrigger>
            </TabsList>
          </div>
          
          {/* Active Applications Tab */}
          <TabsContent value="active" className="mt-0">
            {applications.length === 0 ? (
              <EmptyState 
                isDark={isDark}
                icon={FileText}
                title="No Active Applications"
                description="Start by finding courses that match your profile"
                actionLabel="Find Courses"
                actionLink="/uac-desk/ai-finder"
              />
            ) : (
              <div className="space-y-4">
                {applications.map((app) => (
                  <ActiveApplicationCard
                    key={app.application_id}
                    app={app}
                    stages={stages}
                    isDark={isDark}
                    expandedApp={expandedApp}
                    setExpandedApp={setExpandedApp}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    documents={documents}
                    onCancelClick={() => { setAppToCancel(app); setShowCancelModal(true); }}
                    onChatClick={openCounselorChat}
                    getStageIcon={getStageIcon}
                    getStageColor={getStageColor}
                    formatDate={formatDate}
                    uploading={uploading}
                    onFileUpload={handleFileUpload}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Drafts Tab */}
          <TabsContent value="drafts" className="mt-0">
            {/* Info Banner */}
            <div className={`mb-4 p-4 rounded-xl flex items-start gap-3 ${isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-100'}`}>
              <Info className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                  Draft to Apply
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                  These are courses you're actively preparing to apply for. Select your intake, upload required documents, then submit to your counselor.
                </p>
              </div>
            </div>

            {/* DigiLocker prompt: only when student has drafts AND has not yet verified */}
            {drafts.length > 0 && !digiLockerStatus.verified && (
              <div
                className={`mb-4 p-4 rounded-xl flex items-start gap-3 ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-100'}`}
                data-testid="drafts-digilocker-banner"
              >
                <Shield className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                    Verify your identity documents via DigiLocker
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                    Universities require verified Aadhaar / PAN / academic transcripts. One-tap verification via DigiLocker auto-attaches them to all your applications.
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={handleStartDigiLocker}
                  disabled={initiatingDigiLocker}
                  className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="drafts-digilocker-btn"
                >
                  {initiatingDigiLocker ? (
                    <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Connecting…</>
                  ) : (
                    <><Shield className="w-4 h-4 mr-1.5" /> Verify Now</>
                  )}
                </Button>
              </div>
            )}
            
            {drafts.length === 0 ? (
              <EmptyState 
                isDark={isDark}
                icon={ClipboardList}
                title="No Draft Applications"
                description="Save courses from AI Finder as drafts, or add a university you already have in mind."
                actionLabel="Add Manually"
                actionIcon={Plus}
                onActionClick={() => setShowAddManualModal(true)}
              />
            ) : (
              <div className="space-y-4">
                {drafts.map((draft) => (
                  <DraftCard
                    key={draft.application_id}
                    draft={draft}
                    isDark={isDark}
                    docsReady={docsReady}
                    onIntakeChange={updateDraftIntake}
                    onSubmit={() => { setDraftToSubmit(draft); setShowSubmitModal(true); }}
                    onMoveToBookmarks={() => handleMoveToBookmarks(draft.application_id)}
                    onDelete={() => { setItemToDelete(draft); setShowDeleteModal(true); }}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Bookmarked Tab */}
          <TabsContent value="bookmarked" className="mt-0">
            {/* Info Banner */}
            <div className={`mb-4 p-4 rounded-xl flex items-start gap-3 ${isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-100'}`}>
              <BookmarkCheck className="w-5 h-5 text-purple-500 shrink-0 mt-0.5" />
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-purple-400' : 'text-purple-700'}`}>
                  Saved for Later
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                  Courses you're interested in but not ready to apply yet. Move them to Drafts when you're ready to start the application process.
                </p>
              </div>
            </div>
            
            {bookmarked.length === 0 ? (
              <EmptyState 
                isDark={isDark}
                icon={Bookmark}
                title="No Saved Courses"
                description="Bookmark courses from AI Finder to review later"
                actionLabel="Find Courses"
                actionLink="/uac-desk/ai-finder"
              />
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {bookmarked.map((item) => (
                  <BookmarkedCard
                    key={item.application_id}
                    item={item}
                    isDark={isDark}
                    onMoveToDrafts={() => handleMoveToDrafts(item.application_id)}
                    onDelete={() => { setItemToDelete(item); setShowDeleteModal(true); }}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Offers Tab */}
          <TabsContent value="offers" className="mt-0">
            {/* Info Banner */}
            <div className={`mb-4 p-4 rounded-xl flex items-start gap-3 ${isDark ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-100'}`}>
              <Award className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                  Your Offers
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                  Applications that have received offers from universities. Review and accept your preferred offers to proceed with enrollment.
                </p>
              </div>
            </div>
            
            {(() => {
              const offerApps = applications.filter(app => 
                ['offer_received', 'offer_accepted', 'fee_payment', 'coe_issued', 'visa_applied', 'visa_granted'].includes(app.current_stage)
              );
              
              return offerApps.length === 0 ? (
                <EmptyState 
                  isDark={isDark}
                  icon={Award}
                  title="No Offers Yet"
                  description="Once universities review your applications, offers will appear here"
                  actionLabel="View Active Applications"
                  actionLink="#"
                  onActionClick={() => setMainTab('active')}
                />
              ) : (
                <div className="space-y-4">
                  {offerApps.map((app) => (
                    <ActiveApplicationCard
                      key={app.application_id}
                      app={app}
                      stages={stages}
                      isDark={isDark}
                      expandedApp={expandedApp}
                      setExpandedApp={setExpandedApp}
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                      documents={documents}
                      onCancelClick={() => { setAppToCancel(app); setShowCancelModal(true); }}
                      onChatClick={openCounselorChat}
                      getStageIcon={getStageIcon}
                      getStageColor={getStageColor}
                      formatDate={formatDate}
                      uploading={uploading}
                      onFileUpload={handleFileUpload}
                    />
                  ))}
                </div>
              );
            })()}</TabsContent>
          
          {/* Documents Tab */}
          <TabsContent value="documents" className="mt-0">
            <DocumentsSection
              isDark={isDark}
              documents={documents}
              uploading={uploading}
              onFileUpload={handleFileUpload}
              formatDate={formatDate}
            />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Submit to Counselor Modal */}
      <Dialog open={showSubmitModal} onOpenChange={setShowSubmitModal}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#1a2830] border-white/10' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Submit Application</DialogTitle>
            <DialogDescription className={isDark ? 'text-white/60' : ''}>
              Submit your application to <span className="font-semibold">{draftToSubmit?.university_name}</span> for counselor review.
            </DialogDescription>
          </DialogHeader>
          
          {/* Checklist */}
          <div className="space-y-3">
            <ChecklistItem 
              label="Identity Document" 
              description="Aadhaar or Passport"
              isComplete={documents.aadhaar?.uploaded || documents.passport?.uploaded}
              isDark={isDark}
            />
            <ChecklistItem 
              label="Academic Document" 
              description="10th/12th Marksheet or Transcripts"
              isComplete={documents['10th_marksheet']?.uploaded || documents['12th_marksheet']?.uploaded || documents.transcripts?.uploaded}
              isDark={isDark}
            />
            <ChecklistItem 
              label="Intake Selected" 
              description={draftToSubmit?.intake_term && draftToSubmit?.intake_year ? `${draftToSubmit.intake_term} ${draftToSubmit.intake_year}` : 'Not selected'}
              isComplete={draftToSubmit?.intake_term && draftToSubmit?.intake_year}
              isDark={isDark}
            />
            <ChecklistItem 
              label="English Score (Optional)" 
              description="IELTS/PTE/TOEFL"
              isComplete={documents.english_scorecard?.uploaded}
              isDark={isDark}
              optional
            />
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSubmitModal(false)}>Cancel</Button>
            <Button 
              onClick={handleSubmitDraft}
              disabled={submitting || !docsReady || !draftToSubmit?.intake_term || !draftToSubmit?.intake_year}
              className="bg-ux-cyan hover:bg-ux-cyan/90"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Submit to Counselor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Cancel Application Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#1a2830] border-white/10' : ''}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Cancel Application?
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-white/60' : ''}>
              Cancel your application to <span className="font-semibold">{appToCancel?.university_name}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>Keep</Button>
            <Button onClick={handleCancelApplication} disabled={cancelling} className="bg-red-500 hover:bg-red-600">
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Yes, Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#1a2830] border-white/10' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Remove Course?</DialogTitle>
            <DialogDescription className={isDark ? 'text-white/60' : ''}>
              Remove <span className="font-semibold">{itemToDelete?.university_name}</span> from your list?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            <Button onClick={handleDeleteDraft} disabled={deleting} className="bg-red-500 hover:bg-red-600">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Manual Application Dialog */}
      <Dialog open={showAddManualModal} onOpenChange={setShowAddManualModal}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" data-testid="add-manual-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-ux-cyan" />
              Add Application Manually
            </DialogTitle>
            <DialogDescription>
              Already know which university you want to apply to? Enter the details below to add it directly to your drafts or bookmarks.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Save mode toggle */}
            <div className="flex gap-2 p-1 rounded-lg bg-slate-100">
              <button
                type="button"
                onClick={() => setManualForm({ ...manualForm, status: 'draft' })}
                className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${manualForm.status === 'draft' ? 'bg-white text-slate-900 shadow' : 'text-slate-500'}`}
                data-testid="manual-mode-draft"
              >
                <ClipboardList className="w-4 h-4 inline mr-1.5" /> Save as Draft
              </button>
              <button
                type="button"
                onClick={() => setManualForm({ ...manualForm, status: 'bookmarked' })}
                className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${manualForm.status === 'bookmarked' ? 'bg-white text-slate-900 shadow' : 'text-slate-500'}`}
                data-testid="manual-mode-bookmark"
              >
                <Bookmark className="w-4 h-4 inline mr-1.5" /> Bookmark
              </button>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="manual-university">University name <span className="text-red-500">*</span></Label>
              <Input
                id="manual-university"
                placeholder="e.g. University of Toronto"
                value={manualForm.university_name}
                onChange={(e) => setManualForm({ ...manualForm, university_name: e.target.value })}
                data-testid="manual-university-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="manual-program">Program / Course <span className="text-red-500">*</span></Label>
              <Input
                id="manual-program"
                placeholder="e.g. MSc Computer Science"
                value={manualForm.program}
                onChange={(e) => setManualForm({ ...manualForm, program: e.target.value })}
                data-testid="manual-program-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="manual-country">Country <span className="text-red-500">*</span></Label>
              <Select
                value={manualForm.country}
                onValueChange={(v) => setManualForm({ ...manualForm, country: v })}
              >
                <SelectTrigger id="manual-country" data-testid="manual-country-select">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {['Australia', 'Canada', 'USA', 'UK', 'Germany', 'Ireland', 'New Zealand', 'France', 'Netherlands', 'Singapore', 'Other'].map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="manual-intake-term">Intake term</Label>
                <Select
                  value={manualForm.intake_term}
                  onValueChange={(v) => setManualForm({ ...manualForm, intake_term: v })}
                >
                  <SelectTrigger id="manual-intake-term" data-testid="manual-intake-term">
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    {INTAKE_TERMS.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-intake-year">Intake year</Label>
                <Select
                  value={manualForm.intake_year}
                  onValueChange={(v) => setManualForm({ ...manualForm, intake_year: v })}
                >
                  <SelectTrigger id="manual-intake-year" data-testid="manual-intake-year">
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    {INTAKE_YEARS.map((y) => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="manual-tuition">Tuition fee <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Input
                id="manual-tuition"
                placeholder="e.g. AUD 45,000 / year"
                value={manualForm.tuition_fee}
                onChange={(e) => setManualForm({ ...manualForm, tuition_fee: e.target.value })}
                data-testid="manual-tuition-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="manual-notes">Notes <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Textarea
                id="manual-notes"
                placeholder="Anything you'd like your counselor to know about this application…"
                rows={3}
                value={manualForm.notes}
                onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                data-testid="manual-notes-input"
              />
            </div>

            {/* DigiLocker status inside the dialog */}
            <div
              className={`rounded-lg border p-3 flex items-start gap-3 ${
                digiLockerStatus.verified
                  ? 'border-emerald-200 bg-emerald-50'
                  : 'border-blue-200 bg-blue-50'
              }`}
              data-testid="manual-digilocker-section"
            >
              <Shield className={`w-5 h-5 mt-0.5 shrink-0 ${digiLockerStatus.verified ? 'text-emerald-600' : 'text-blue-600'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900">
                  {digiLockerStatus.verified
                    ? `Identity verified via DigiLocker (${digiLockerStatus.count} doc${digiLockerStatus.count === 1 ? '' : 's'})`
                    : 'Verify identity documents via DigiLocker'}
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                  {digiLockerStatus.verified
                    ? 'Your Aadhaar / academic docs will auto-attach to this application.'
                    : 'Recommended before submission. Most universities require verified ID + transcripts.'}
                </p>
                {!digiLockerStatus.verified && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleStartDigiLocker}
                    disabled={initiatingDigiLocker}
                    className="mt-2 h-8 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                    data-testid="manual-digilocker-btn"
                  >
                    {initiatingDigiLocker ? (
                      <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Connecting…</>
                    ) : (
                      <>Verify via DigiLocker</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAddManualModal(false)}
              disabled={creatingManual}
              data-testid="manual-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateManualApplication}
              disabled={creatingManual}
              className="bg-ux-cyan hover:bg-ux-cyan/90 text-white"
              data-testid="manual-create-btn"
            >
              {creatingManual ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating…</>
              ) : (
                <>{manualForm.status === 'draft' ? 'Create Draft' : 'Add to Bookmarks'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ Sub-Components ============

const EmptyState = ({ isDark, icon: Icon, title, description, actionLabel, actionLink, onActionClick, actionIcon: ActionIcon = Search }) => (
  <Card className={`${isDark ? 'bg-white/5 border-white/10' : ''}`}>
    <CardContent className="p-12 text-center">
      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${isDark ? 'bg-white/10' : 'bg-slate-100'}`}>
        <Icon className={`w-8 h-8 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
      </div>
      <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{title}</h3>
      <p className={`text-sm mb-6 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>{description}</p>
      {onActionClick ? (
        <Button 
          className="bg-ux-cyan hover:bg-ux-cyan/90 text-white rounded-full px-8"
          onClick={onActionClick}
        >
          <ActionIcon className="w-4 h-4 mr-2" /> {actionLabel}
        </Button>
      ) : (
        <Link to={actionLink}>
          <Button className="bg-ux-cyan hover:bg-ux-cyan/90 text-white rounded-full px-8">
            <ActionIcon className="w-4 h-4 mr-2" /> {actionLabel}
          </Button>
        </Link>
      )}
    </CardContent>
  </Card>
);

const ChecklistItem = ({ label, description, isComplete, isDark, optional }) => (
  <div className={`flex items-center gap-3 p-3 rounded-lg ${
    isComplete 
      ? isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'
      : isDark ? 'bg-white/5' : 'bg-slate-50'
  }`}>
    {isComplete ? (
      <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
    ) : (
      <div className={`w-5 h-5 rounded-full border-2 shrink-0 ${isDark ? 'border-white/30' : 'border-slate-300'}`} />
    )}
    <div className="flex-1">
      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
        {label} {optional && <span className="text-xs text-slate-400">(Optional)</span>}
      </p>
      <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>{description}</p>
    </div>
  </div>
);

const ActiveApplicationCard = ({ 
  app, stages, isDark, expandedApp, setExpandedApp, activeTab, setActiveTab,
  documents, onCancelClick, onChatClick, getStageIcon, getStageColor, formatDate,
  uploading, onFileUpload
}) => {
  const stageInfo = stages.find(s => s.id === app.current_stage);
  const StageIcon = getStageIcon(app.current_stage);
  const isExpanded = expandedApp === app.application_id;
  const canCancel = PRE_REVIEW_STAGES.includes(app.current_stage);
  
  return (
    <Card className={`overflow-hidden transition-all ${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white shadow-sm'}`}>
      <CardContent className="p-0">
        <div className="p-4 cursor-pointer" onClick={() => setExpandedApp(isExpanded ? null : app.application_id)}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-ux-cyan/10`}>
              <Building className="w-6 h-6 text-ux-cyan" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className={`font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {app.university_name}
                </h3>
                <Badge className={`${getStageColor(stageInfo)} border shrink-0`}>
                  <StageIcon className="w-3 h-3 mr-1" />
                  {stageInfo?.name || app.current_stage}
                </Badge>
              </div>
              
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className={`flex items-center gap-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                  <GraduationCap className="w-3.5 h-3.5" /> {app.program}
                </span>
                {app.tuition_fee && (
                  <span className={`flex items-center gap-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                    <DollarSign className="w-3.5 h-3.5" /> {app.tuition_fee}
                  </span>
                )}
                <span className={`flex items-center gap-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                  <Calendar className="w-3.5 h-3.5" /> {app.intake || 'TBD'}
                </span>
              </div>
              
              <div className="mt-3">
                <Progress value={app.progress_percent || 0} className="h-1.5 [&>div]:bg-gradient-to-r [&>div]:from-ux-cyan [&>div]:to-blue-500" />
              </div>
            </div>
            
            <ChevronDown className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''} ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
          </div>
        </div>
        
        {isExpanded && (
          <div className={`border-t ${isDark ? 'border-white/10' : 'border-slate-100'} p-4`}>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" className="rounded-full" onClick={onChatClick}>
                <MessageCircle className="w-4 h-4 mr-1.5" /> Chat with Counselor
              </Button>
              {canCancel && (
                <Button variant="outline" size="sm" className="rounded-full text-red-500 border-red-200" onClick={onCancelClick}>
                  <XCircle className="w-4 h-4 mr-1.5" /> Cancel
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DraftCard = ({ draft, isDark, docsReady, onIntakeChange, onSubmit, onMoveToBookmarks, onDelete, formatDate }) => (
  <Card className={`overflow-hidden ${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white shadow-sm'}`}>
    <CardContent className="p-4">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-amber-500/10`}>
          <Building className="w-6 h-6 text-amber-500" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {draft.university_name}
              </h3>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm mt-1">
                <span className={`flex items-center gap-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                  <GraduationCap className="w-3.5 h-3.5" /> {draft.program}
                </span>
                {draft.tuition_fee && (
                  <span className={`flex items-center gap-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                    <DollarSign className="w-3.5 h-3.5" /> {draft.tuition_fee}
                  </span>
                )}
                {draft.match_score && (
                  <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200">
                    <Sparkles className="w-3 h-3 mr-1" /> {draft.match_score}% Match
                  </Badge>
                )}
              </div>
            </div>
            <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">
              Draft
            </Badge>
          </div>
          
          {/* Intake Selection */}
          <div className="flex flex-wrap items-center gap-2 mt-3 mb-3">
            <span className={`text-xs font-medium ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Intake:</span>
            <Select 
              value={draft.intake_term || ''} 
              onValueChange={(v) => onIntakeChange(draft.application_id, 'intake_term', v)}
            >
              <SelectTrigger className="w-28 h-8 text-xs">
                <SelectValue placeholder="Term" />
              </SelectTrigger>
              <SelectContent>
                {INTAKE_TERMS.map(term => (
                  <SelectItem key={term} value={term}>{term}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select 
              value={draft.intake_year || ''} 
              onValueChange={(v) => onIntakeChange(draft.application_id, 'intake_year', v)}
            >
              <SelectTrigger className="w-20 h-8 text-xs">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {INTAKE_YEARS.map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <Button 
              size="sm" 
              className={`rounded-full ${docsReady && draft.intake_term && draft.intake_year ? 'bg-ux-cyan hover:bg-ux-cyan/90' : 'bg-slate-300 cursor-not-allowed'}`}
              onClick={onSubmit}
              disabled={!docsReady || !draft.intake_term || !draft.intake_year}
            >
              <Send className="w-3.5 h-3.5 mr-1.5" /> Submit to Counselor
            </Button>
            <Button variant="outline" size="sm" className="rounded-full" onClick={onMoveToBookmarks}>
              <Bookmark className="w-3.5 h-3.5 mr-1.5" /> Save for Later
            </Button>
            <Button variant="ghost" size="sm" className="rounded-full text-red-500" onClick={onDelete}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const BookmarkedCard = ({ item, isDark, onMoveToDrafts, onDelete, formatDate }) => (
  <Card className={`${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white shadow-sm'}`}>
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-purple-500/10`}>
          <Building className="w-5 h-5 text-purple-500" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {item.university_name}
          </h4>
          <p className={`text-xs mt-0.5 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
            {item.program}
          </p>
          {item.tuition_fee && (
            <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
              {item.tuition_fee}
            </p>
          )}
          
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" className="rounded-full text-xs h-7 px-3" onClick={onMoveToDrafts}>
              <ArrowRight className="w-3 h-3 mr-1" /> Move to Drafts
            </Button>
            <Button size="sm" variant="ghost" className="rounded-full text-xs h-7 px-2 text-red-500" onClick={onDelete}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const DocumentsSection = ({ isDark, documents, uploading, onFileUpload, formatDate }) => (
  <div className="space-y-4">
    {/* Required Documents Info */}
    <Card className={`${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-100'}`}>
      <CardContent className="p-4">
        <h3 className={`font-semibold mb-2 ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
          Required Documents for Application
        </h3>
        <ul className={`text-sm space-y-1 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-blue-500" />
            <strong>Identity:</strong> Aadhaar Card OR Passport (at least 1)
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-blue-500" />
            <strong>Academic (UG):</strong> 10th OR 12th Marksheet
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-blue-500" />
            <strong>Academic (PG):</strong> 12th Marksheet OR Degree OR Transcripts
          </li>
          <li className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <strong>English Score:</strong> IELTS/PTE/TOEFL (Optional but recommended)
          </li>
        </ul>
      </CardContent>
    </Card>
    
    {/* Document List */}
    <div className="space-y-2">
      {Object.entries(DOCUMENT_TYPES).map(([docType, docInfo]) => {
        const docData = documents[docType];
        const isUploaded = docData?.uploaded;
        const isUploadingThis = uploading === docType;
        const DocIcon = docInfo.icon;
        
        return (
          <div
            key={docType}
            className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
              isUploaded 
                ? isDark ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-100'
                : isDark ? 'bg-white/5 border border-white/5' : 'bg-white border border-slate-100'
            }`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
              isUploaded ? 'bg-emerald-100 dark:bg-emerald-500/20' : isDark ? 'bg-white/10' : 'bg-slate-100'
            }`}>
              {isUploaded ? (
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              ) : (
                <DocIcon className={`w-5 h-5 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {docInfo.name}
              </span>
              {isUploaded && docData.filename && (
                <p className={`text-xs truncate ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                  {docData.filename} • {formatDate(docData.uploaded_at)}
                </p>
              )}
            </div>
            
            <div className="shrink-0">
              {isUploaded ? (
                docData.source === 'digilocker' ? (
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                      isDark
                        ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                        : 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                    }`}
                    data-testid={`doc-status-${docType}`}
                    title="Verified via DigiLocker"
                  >
                    <Shield className="w-3 h-3" />
                    DigiLocker Verified
                  </span>
                ) : (
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                      isDark
                        ? 'bg-sky-500/15 text-sky-300 border border-sky-500/30'
                        : 'bg-sky-50 text-sky-700 border border-sky-200'
                    }`}
                    data-testid={`doc-status-${docType}`}
                    title="Uploaded manually"
                  >
                    <CheckCircle className="w-3 h-3" />
                    Uploaded
                  </span>
                )
              ) : (
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => onFileUpload(docType, e.target.files[0])}
                    disabled={isUploadingThis}
                  />
                  <Button variant="outline" size="sm" className="h-8 px-3 text-xs border-ux-cyan text-ux-cyan" asChild disabled={isUploadingThis}>
                    <span>
                      {isUploadingThis ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                      Upload
                    </span>
                  </Button>
                </label>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

export default ApplicationTrackerPage;
